import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import {
  X,
  Save,
  Settings,
  Target,
  Share2,
  Code,
  Trash2,
  Loader2,
  BarChart3,
  Wand2,
  Link2,
  CheckCircle,
  Heart,
  BookOpen,
  ShieldQuestion,
} from 'lucide-react';
import MediumStyleEditor from '@/components/articles/MediumStyleEditor';
import { SeoAnalysisDialog } from '@/components/articles/SeoAnalysisDialog';
import { EeatCheckerDialog } from '@/components/articles/EeatCheckerDialog';
import { HelpfulContentDialog } from '@/components/articles/HelpfulContentDialog';
import { KeyTakeawaysDrawer } from '@/components/articles/KeyTakeawaysDrawer';
import { ArticleFAQDrawer } from '@/components/articles/ArticleFAQDrawer';
import { ArticleSourcesDrawer } from '@/components/articles/ArticleSourcesDrawer';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { analyzeArticleSEO, getScoreColor, getScoreBgColor } from '@/utils/seoAnalyzer';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';
import type { ArticleSource, ArticleFAQ, ArticleKeyTakeaways, SEOCheck } from '@/types/articles';

const SCHEMA_TYPES = ['CollectionPage', 'WebPage', 'Article', 'ItemList'];

export interface PillarPageFields {
  content: string;
  excerpt: string;
  featuredImageUrl: string;
  featuredImageAlt: string;
  metaTitle: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  focusKeyword: string;
  noindex: boolean;
  canonicalUrl: string;
  schemaType: string;
}

interface PillarPageEditorProps {
  title: string;
  subtitle?: string;
  fields: PillarPageFields;
  onFieldChange: <K extends keyof PillarPageFields>(field: K, value: PillarPageFields[K]) => void;
  onSave: () => void;
  onClose: () => void;
  isSaving?: boolean;
}

const CheckItem = ({ check }: { check: SEOCheck }) => {
  const icons = {
    good: <CheckCircle className="h-4 w-4 text-green-600" />,
    warning: <BarChart3 className="h-4 w-4 text-yellow-600" />,
    error: <X className="h-4 w-4 text-red-600" />,
  };
  return (
    <div className="flex items-start gap-3 py-2">
      {icons[check.status]}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{check.label}</p>
        <p className="text-xs text-muted-foreground">{check.message}</p>
      </div>
    </div>
  );
};

const ScoreCircle = ({ score, label }: { score: number; label: string }) => (
  <div className="flex flex-col items-center">
    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getScoreBgColor(score)} bg-opacity-20`}>
      <span className={`text-xl font-bold ${getScoreColor(score)}`}>{score}</span>
    </div>
    <span className="text-xs text-muted-foreground mt-1">{label}</span>
  </div>
);

const rightToolsDef = [
  { id: 'eeat', label: 'EEAT Checker', icon: CheckCircle },
  { id: 'helpful', label: 'Helpful Content', icon: Heart },
  { id: 'takeaways', label: 'Key Takeaways', icon: BookOpen },
  { id: 'faqs', label: 'FAQs', icon: ShieldQuestion },
  { id: 'sources', label: 'Sources', icon: Link2 },
  { id: 'seo-analyze', label: 'SEO Analyze', icon: BarChart3 },
  { id: 'seo-optimize', label: 'SEO Optimize', icon: Wand2 },
  { id: 'internal-links', label: 'Internal Links', icon: Link2 },
];

export const PillarPageEditor: React.FC<PillarPageEditorProps> = ({
  title,
  subtitle,
  fields,
  onFieldChange,
  onSave,
  onClose,
  isSaving = false,
}) => {
  const { currentBrandId: selectedBrandId } = useBrandManager();
  const [activeTab, setActiveTab] = useState('general');

  // SEO tool states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [seoDialogOpen, setSeoDialogOpen] = useState(false);
  const [seoDialogType, setSeoDialogType] = useState<'analyze' | 'optimize'>('analyze');
  const [seoDialogData, setSeoDialogData] = useState<any>(null);

  // EEAT
  const [eeatDialogOpen, setEeatDialogOpen] = useState(false);
  const [eeatData, setEeatData] = useState<any>(null);
  const [isCheckingEeat, setIsCheckingEeat] = useState(false);
  const [isFixingEeat, setIsFixingEeat] = useState(false);

  // Helpful Content
  const [helpfulDialogOpen, setHelpfulDialogOpen] = useState(false);
  const [helpfulData, setHelpfulData] = useState<any>(null);
  const [isCheckingHelpful, setIsCheckingHelpful] = useState(false);
  const [isFixingHelpful, setIsFixingHelpful] = useState(false);

  // Key Takeaways
  const [takeawaysDialogOpen, setTakeawaysDialogOpen] = useState(false);
  const [takeawaysData, setTakeawaysData] = useState<any>(null);
  const [isGeneratingTakeaways, setIsGeneratingTakeaways] = useState(false);
  const [keyTakeaways, setKeyTakeaways] = useState<ArticleKeyTakeaways | null>(null);

  // FAQs & Sources
  const [faqPanelOpen, setFaqPanelOpen] = useState(false);
  const [faqs, setFaqs] = useState<ArticleFAQ[]>([]);
  const [sourcesPanelOpen, setSourcesPanelOpen] = useState(false);
  const [sources, setSources] = useState<ArticleSource[]>([]);

  // Debounced SEO analysis
  const debouncedContent = useDebounce(fields.content, 500);
  const debouncedMetaTitle = useDebounce(fields.metaTitle, 500);
  const debouncedMetaDescription = useDebounce(fields.metaDescription, 500);
  const debouncedFocusKeyword = useDebounce(fields.focusKeyword, 500);

  const seoAnalysis = useMemo(() => {
    return analyzeArticleSEO(
      title, subtitle || '', debouncedContent,
      debouncedMetaTitle, debouncedMetaDescription, debouncedFocusKeyword
    );
  }, [title, subtitle, debouncedContent, debouncedMetaTitle, debouncedMetaDescription, debouncedFocusKeyword]);

  const keyphraseChecks = seoAnalysis.checks.filter(c => c.category === 'keyphrase');
  const contentChecks = seoAnalysis.checks.filter(c => c.category === 'content');
  const readabilityChecks = seoAnalysis.checks.filter(c => c.category === 'readability');

  // Link added handler
  const handleLinkAdded = useCallback((url: string, text: string) => {
    const exists = sources.some(source => source.link === url);
    if (!exists) {
      setSources(prev => [...prev, { title: text || url, description: '', link: url }]);
    }
  }, [sources]);

  // Sync links from content
  const handleSyncLinksFromArticle = useCallback(() => {
    if (!fields.content) { toast.info('No content to extract links from'); return; }
    const parser = new DOMParser();
    const doc = parser.parseFromString(fields.content, 'text/html');
    const links = doc.querySelectorAll('a[href]');
    let addedCount = 0;
    const newSources = [...sources];
    links.forEach((link) => {
      const url = link.getAttribute('href');
      const text = link.textContent?.trim() || '';
      if (!url || url.startsWith('#') || url.startsWith('mailto:') || url.startsWith('tel:')) return;
      const exists = newSources.some(source => source.link === url);
      if (!exists) { newSources.push({ title: text || url, description: '', link: url }); addedCount++; }
    });
    if (addedCount > 0) { setSources(newSources); toast.success(`Added ${addedCount} new source${addedCount > 1 ? 's' : ''}`); }
    else { toast.info('No new links found'); }
  }, [fields.content, sources]);

  // SEO Analyze
  const handleSeoAnalyze = async () => {
    if (!fields.content || fields.content.trim().length < 50) { toast.error('Add content (at least 50 chars) before analyzing'); return; }
    if (!selectedBrandId) { toast.error('Please select a brand'); return; }
    setIsAnalyzing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      const { data, error } = await supabase.functions.invoke('seo-machine', {
        body: { action: 'analyze', content: fields.content, title, brandId: selectedBrandId, userId: user.id }
      });
      if (error) throw new Error(error.message || 'Failed to analyze content');
      if (data) { setSeoDialogType('analyze'); setSeoDialogData(data); setSeoDialogOpen(true); toast.success('Analysis complete!'); }
    } catch (error: any) { console.error('Error analyzing:', error); toast.error(error.message || 'Failed to analyze'); }
    finally { setIsAnalyzing(false); }
  };

  // SEO Optimize
  const handleSeoOptimize = async () => {
    if (!fields.content || fields.content.trim().length < 50) { toast.error('Add content (at least 50 chars) before optimizing'); return; }
    if (!selectedBrandId) { toast.error('Please select a brand'); return; }
    setIsOptimizing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      const { data, error } = await supabase.functions.invoke('seo-machine', {
        body: { action: 'optimize', content: fields.content, title, brandId: selectedBrandId, userId: user.id }
      });
      if (error) throw new Error(error.message || 'Failed to optimize content');
      if (data) {
        if (data.metaTitle) onFieldChange('metaTitle', data.metaTitle);
        if (data.metaDescription) onFieldChange('metaDescription', data.metaDescription);
        setSeoDialogType('optimize'); setSeoDialogData(data); setSeoDialogOpen(true); toast.success('Optimization complete!');
      }
    } catch (error: any) { console.error('Error optimizing:', error); toast.error(error.message || 'Failed to optimize'); }
    finally { setIsOptimizing(false); }
  };

  // EEAT Check
  const handleEeatCheck = async () => {
    if (!fields.content || fields.content.trim().length < 50) { toast.error('Add content (at least 50 chars) before checking EEAT'); return; }
    setIsCheckingEeat(true);
    try {
      const { data, error } = await supabase.functions.invoke('eeat-checker', {
        body: { content: fields.content, title, brandId: selectedBrandId, action: 'check' }
      });
      if (error) throw error;
      if (data?.success) { setEeatData(data); setEeatDialogOpen(true); toast.success(`EEAT Check complete! Score: ${data.averageScore}/100`); }
      else if (data?.error) { toast.error(data.error); }
    } catch (error: any) { console.error('EEAT check error:', error); toast.error(error.message || 'Failed to run EEAT check'); }
    finally { setIsCheckingEeat(false); }
  };

  const handleEeatFix = async () => {
    if (!fields.content || fields.content.trim().length < 50) { toast.error('No content to fix'); return; }
    setIsFixingEeat(true);
    try {
      const { data, error } = await supabase.functions.invoke('eeat-checker', {
        body: { content: fields.content, title, brandId: selectedBrandId, action: 'fix' }
      });
      if (error) throw error;
      if (data?.rewrittenContent) { onFieldChange('content', data.rewrittenContent); setEeatDialogOpen(false); toast.success('Content rewritten with improved EEAT!'); }
      else if (data?.error) { toast.error(data.error); }
    } catch (error: any) { console.error('EEAT fix error:', error); toast.error(error.message || 'Failed to fix'); }
    finally { setIsFixingEeat(false); }
  };

  // Helpful Content Check
  const handleHelpfulCheck = async () => {
    if (!fields.content || fields.content.trim().length < 50) { toast.error('Add content (at least 50 chars) before checking'); return; }
    setIsCheckingHelpful(true);
    try {
      const { data, error } = await supabase.functions.invoke('helpful-content-checker', {
        body: { content: fields.content, title, brandId: selectedBrandId, action: 'check' }
      });
      if (error) throw error;
      if (data?.success) { setHelpfulData(data); setHelpfulDialogOpen(true); toast.success(`Helpful Content Check complete! Score: ${data.averageScore}/100`); }
      else if (data?.error) { toast.error(data.error); }
    } catch (error: any) { console.error('Helpful content check error:', error); toast.error(error.message || 'Failed to run check'); }
    finally { setIsCheckingHelpful(false); }
  };

  const handleHelpfulFix = async () => {
    if (!fields.content || fields.content.trim().length < 50) { toast.error('No content to fix'); return; }
    setIsFixingHelpful(true);
    try {
      const { data, error } = await supabase.functions.invoke('helpful-content-checker', {
        body: { content: fields.content, title, brandId: selectedBrandId, action: 'fix' }
      });
      if (error) throw error;
      if (data?.rewrittenContent) { onFieldChange('content', data.rewrittenContent); setHelpfulDialogOpen(false); toast.success('Content rewritten with improved helpfulness!'); }
      else if (data?.error) { toast.error(data.error); }
    } catch (error: any) { console.error('Helpful fix error:', error); toast.error(error.message || 'Failed to fix'); }
    finally { setIsFixingHelpful(false); }
  };

  // Key Takeaways
  const handleKeyTakeaways = async () => {
    if (!fields.content || fields.content.trim().length < 50) { toast.error('Add content before generating takeaways'); return; }
    setIsGeneratingTakeaways(true);
    try {
      const { data, error } = await supabase.functions.invoke('key-takeaways-generator', {
        body: { content: fields.content, title, brandId: selectedBrandId }
      });
      if (error) throw error;
      if (data?.success) { setTakeawaysData(data); setTakeawaysDialogOpen(true); toast.success('Key takeaways generated!'); }
      else if (data?.error) { toast.error(data.error); }
    } catch (error: any) { console.error('Key takeaways error:', error); toast.error(error.message || 'Failed to generate takeaways'); }
    finally { setIsGeneratingTakeaways(false); }
  };

  const handleAddTakeawaysToArticle = (takeawaysPayload: ArticleKeyTakeaways, placement: 'top' | 'bottom') => {
    const takeawaysHtml = `<div class="key-takeaways-box">
<h4 style="margin: 0 0 0.5rem 0; font-size: 0.8rem; font-weight: 600;">✨ Key Takeaways</h4>
<p style="font-size: 0.75rem; color: #444; margin: 0 0 0.4rem 0; line-height: 1.5;">${takeawaysPayload.summary}</p>
<ul style="margin: 0; padding-left: 1.1rem; font-size: 0.75rem; line-height: 1.5;">
${takeawaysPayload.takeaways.map(t => `<li style="margin-bottom: 0.25rem; font-size: 0.75rem;"><strong>${t.title}</strong> — ${t.description}</li>`).join('\n')}
</ul>
<p style="font-size: 0.65rem; color: #666; margin: 0.4rem 0 0 0; font-style: italic;">Reading time: ${takeawaysPayload.readingTime} · Audience: ${takeawaysPayload.targetAudience} · Level: ${takeawaysPayload.difficulty}</p>
</div>`;
    const currentContent = fields.content || '';
    const newContent = placement === 'top' ? takeawaysHtml + '\n' + currentContent : currentContent + '\n' + takeawaysHtml;
    onFieldChange('content', newContent);
    setKeyTakeaways({ ...takeawaysPayload, placement });
  };

  // Right toolbar click handler
  const handleIconClick = (toolId: string) => {
    if (toolId === 'eeat') { handleEeatCheck(); }
    else if (toolId === 'helpful') { handleHelpfulCheck(); }
    else if (toolId === 'takeaways') {
      if (takeawaysData) { setTakeawaysDialogOpen(!takeawaysDialogOpen); }
      else { handleKeyTakeaways(); }
    }
    else if (toolId === 'faqs') { setFaqPanelOpen(!faqPanelOpen); }
    else if (toolId === 'sources') { setSourcesPanelOpen(!sourcesPanelOpen); }
    else if (toolId === 'seo-analyze') { handleSeoAnalyze(); }
    else if (toolId === 'seo-optimize') { handleSeoOptimize(); }
    else { setActiveTab(toolId); }
  };

  const isToolActive = (toolId: string) => {
    if (toolId === 'takeaways') return takeawaysDialogOpen;
    if (toolId === 'faqs') return faqPanelOpen;
    if (toolId === 'sources') return sourcesPanelOpen;
    if (toolId === 'seo-analyze') return seoDialogOpen && seoDialogType === 'analyze';
    if (toolId === 'seo-optimize') return seoDialogOpen && seoDialogType === 'optimize';
    return false;
  };

  const isToolLoading = (toolId: string) => {
    if (toolId === 'eeat') return isCheckingEeat;
    if (toolId === 'helpful') return isCheckingHelpful;
    if (toolId === 'takeaways') return isGeneratingTakeaways;
    if (toolId === 'seo-analyze') return isAnalyzing;
    if (toolId === 'seo-optimize') return isOptimizing;
    return false;
  };

  return (
    <>
      <div className="fixed inset-0 z-[60] flex overflow-hidden">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        
        {/* Panel */}
        <div className="relative ml-auto flex h-full w-full bg-background border-l shadow-2xl overflow-hidden" style={{ maxWidth: '100vw' }}>
          <div className="h-full flex flex-col overflow-hidden w-full">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-background shrink-0">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                  <h1 className="text-lg font-semibold truncate">{title}</h1>
                  {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
                </div>
              </div>
              <Button onClick={onSave} disabled={isSaving} className="shrink-0">
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>

            {/* Main Content Area */}
            <div className="flex flex-1 min-w-0 overflow-hidden">
              <ResizablePanelGroup direction="horizontal" className="min-w-0 flex-1">
                {/* Editor Panel */}
                <ResizablePanel defaultSize={60} minSize={40}>
                  <div className="h-full overflow-y-auto p-6">
                    <div className="max-w-4xl mx-auto space-y-6">
                      {/* Excerpt */}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">Excerpt</Label>
                        <Textarea
                          placeholder="Brief summary of the pillar page..."
                          value={fields.excerpt}
                          onChange={(e) => onFieldChange('excerpt', e.target.value)}
                          rows={2}
                          className="resize-none"
                        />
                      </div>

                      {/* Content Editor */}
                      <div className="min-h-[500px]">
                        <MediumStyleEditor
                          value={fields.content}
                          onChange={(value) => onFieldChange('content', value)}
                          placeholder="Write your pillar page content..."
                          onLinkAdded={handleLinkAdded}
                        />
                      </div>
                    </div>
                  </div>
                </ResizablePanel>

                <ResizableHandle withHandle />

                {/* SEO Sidebar */}
                <ResizablePanel defaultSize={25} minSize={20} maxSize={35}>
                  <div className="h-full bg-muted/30 flex flex-col overflow-hidden">
                    {/* Scores */}
                    <div className="p-4 border-b bg-background shrink-0">
                      <div className="flex justify-around">
                        <ScoreCircle score={seoAnalysis.seoScore} label="SEO Score" />
                        <ScoreCircle score={seoAnalysis.readabilityScore} label="Readability" />
                      </div>
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                      <TabsList className="w-full justify-start rounded-none border-b bg-background px-4 shrink-0 overflow-x-auto overflow-y-hidden">
                        <TabsTrigger value="general" className="gap-2">
                          <Settings className="h-3.5 w-3.5" />
                          General
                        </TabsTrigger>
                        <TabsTrigger value="seo" className="gap-2">
                          <Target className="h-3.5 w-3.5" />
                          SEO
                        </TabsTrigger>
                        <TabsTrigger value="social" className="gap-2">
                          <Share2 className="h-3.5 w-3.5" />
                          Social
                        </TabsTrigger>
                        <TabsTrigger value="schema" className="gap-2">
                          <Code className="h-3.5 w-3.5" />
                          Schema
                        </TabsTrigger>
                      </TabsList>

                      <div className="flex-1 overflow-y-auto">
                        {/* General Tab */}
                        <TabsContent value="general" className="m-0 p-4 space-y-4">
                          <div className="space-y-2">
                            <Label>Focus Keyphrase</Label>
                            <Input placeholder="Enter focus keyphrase" value={fields.focusKeyword} onChange={(e) => onFieldChange('focusKeyword', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label>Featured Image</Label>
                            {fields.featuredImageUrl ? (
                              <div className="space-y-3">
                                <div className="relative rounded-lg overflow-hidden border bg-muted">
                                  <img src={fields.featuredImageUrl} alt={fields.featuredImageAlt || 'Featured image'} className="w-full h-40 object-cover" />
                                  <Button variant="destructive" size="icon" className="absolute top-2 right-2 h-7 w-7" onClick={() => { onFieldChange('featuredImageUrl', ''); onFieldChange('featuredImageAlt', ''); }}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-xs">Alt Text</Label>
                                  <Input placeholder="Describe the image" value={fields.featuredImageAlt} onChange={(e) => onFieldChange('featuredImageAlt', e.target.value)} className="h-8 text-sm" />
                                </div>
                              </div>
                            ) : (
                              <Input placeholder="Featured image URL..." value={fields.featuredImageUrl} onChange={(e) => onFieldChange('featuredImageUrl', e.target.value)} />
                            )}
                          </div>
                          <Separator />
                          <div className="flex items-center justify-between">
                            <div>
                              <Label>No Index</Label>
                              <p className="text-xs text-muted-foreground">Hide from search engines</p>
                            </div>
                            <Switch checked={fields.noindex} onCheckedChange={(v) => onFieldChange('noindex', v)} />
                          </div>
                        </TabsContent>

                        {/* SEO Tab */}
                        <TabsContent value="seo" className="m-0 p-4 space-y-4">
                          <div className="space-y-2">
                            <Label>Meta Title</Label>
                            <Input placeholder="SEO title (50-60 characters)" value={fields.metaTitle} onChange={(e) => onFieldChange('metaTitle', e.target.value)} />
                            <p className="text-xs text-muted-foreground">{fields.metaTitle.length}/60 characters</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Meta Description</Label>
                            <Textarea placeholder="SEO description (150-160 characters)" value={fields.metaDescription} onChange={(e) => onFieldChange('metaDescription', e.target.value)} rows={3} />
                            <p className="text-xs text-muted-foreground">{fields.metaDescription.length}/160 characters</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Canonical URL</Label>
                            <Input placeholder="https://..." value={fields.canonicalUrl} onChange={(e) => onFieldChange('canonicalUrl', e.target.value)} />
                          </div>

                          <Separator />

                          {/* Keyphrase Analysis */}
                          <div>
                            <h4 className="font-medium mb-2 flex items-center gap-2"><Target className="h-4 w-4" />Keyphrase Analysis</h4>
                            <div className="space-y-1">{keyphraseChecks.map((check, i) => <CheckItem key={i} check={check} />)}</div>
                          </div>
                          <Separator />
                          <div>
                            <h4 className="font-medium mb-2">Content Analysis</h4>
                            <div className="space-y-1">{contentChecks.map((check, i) => <CheckItem key={i} check={check} />)}</div>
                          </div>
                          <Separator />
                          <div>
                            <h4 className="font-medium mb-2">Readability</h4>
                            <div className="space-y-1">{readabilityChecks.map((check, i) => <CheckItem key={i} check={check} />)}</div>
                          </div>
                        </TabsContent>

                        {/* Social Tab */}
                        <TabsContent value="social" className="m-0 p-4 space-y-4">
                          <div className="space-y-2">
                            <Label>OG Title</Label>
                            <Input placeholder="Open Graph title" value={fields.ogTitle} onChange={(e) => onFieldChange('ogTitle', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label>OG Description</Label>
                            <Textarea placeholder="Open Graph description" value={fields.ogDescription} onChange={(e) => onFieldChange('ogDescription', e.target.value)} rows={3} />
                          </div>
                          <div className="space-y-2">
                            <Label>OG Image URL</Label>
                            <Input placeholder="https://..." value={fields.ogImage} onChange={(e) => onFieldChange('ogImage', e.target.value)} />
                            {fields.ogImage && (
                              <div className="relative rounded-lg overflow-hidden border bg-muted">
                                <img src={fields.ogImage} alt="OG preview" className="w-full h-32 object-cover" />
                              </div>
                            )}
                          </div>
                        </TabsContent>

                        {/* Schema Tab */}
                        <TabsContent value="schema" className="m-0 p-4 space-y-4">
                          <div className="space-y-2">
                            <Label>Schema Type</Label>
                            <Select value={fields.schemaType} onValueChange={(v) => onFieldChange('schemaType', v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>{SCHEMA_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <Separator />
                          <div>
                            <h4 className="font-medium mb-2">Schema Preview</h4>
                            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-[200px]">
{JSON.stringify({ "@context": "https://schema.org", "@type": fields.schemaType, "headline": fields.metaTitle || title, "description": fields.metaDescription, "image": fields.featuredImageUrl || fields.ogImage }, null, 2)}
                            </pre>
                          </div>
                        </TabsContent>
                      </div>
                    </Tabs>
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>

              {/* Right Icon Sidebar */}
              <TooltipProvider delayDuration={200}>
                <div className="flex flex-col border-l bg-muted/20 w-12 shrink-0">
                  {rightToolsDef.map(tool => (
                    <Tooltip key={tool.id}>
                      <TooltipTrigger asChild>
                        <button
                          disabled={isToolLoading(tool.id)}
                          onClick={() => handleIconClick(tool.id)}
                          className={`flex items-center justify-center h-12 w-12 border-b transition-colors hover:bg-accent disabled:opacity-50 disabled:pointer-events-none ${
                            isToolActive(tool.id) ? 'bg-accent text-primary' : 'text-muted-foreground'
                          }`}
                        >
                          {isToolLoading(tool.id)
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <tool.icon className="h-4 w-4" />
                          }
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left"><p>{tool.label}</p></TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Analysis/Optimization Dialog */}
      <SeoAnalysisDialog open={seoDialogOpen} onOpenChange={setSeoDialogOpen} type={seoDialogType} data={seoDialogData} />

      {/* EEAT Checker Dialog */}
      <EeatCheckerDialog open={eeatDialogOpen} onOpenChange={setEeatDialogOpen} data={eeatData} onFix={handleEeatFix} isFixing={isFixingEeat} />

      {/* Helpful Content Dialog */}
      <HelpfulContentDialog open={helpfulDialogOpen} onOpenChange={setHelpfulDialogOpen} data={helpfulData} onFix={handleHelpfulFix} isFixing={isFixingHelpful} />

      {/* Key Takeaways Drawer */}
      <KeyTakeawaysDrawer
        open={takeawaysDialogOpen}
        onOpenChange={setTakeawaysDialogOpen}
        data={takeawaysData}
        onAddToArticle={handleAddTakeawaysToArticle}
        isAddedToArticle={!!keyTakeaways}
        onRemoveFromArticle={() => {
          const content = fields.content || '';
          const cleaned = content.replace(/<div class="key-takeaways-box">[\s\S]*?<\/div>/gi, '').trim();
          onFieldChange('content', cleaned);
          setKeyTakeaways(null);
        }}
      />

      {/* FAQ Drawer */}
      <ArticleFAQDrawer
        open={faqPanelOpen}
        onOpenChange={setFaqPanelOpen}
        focusKeyword={fields.focusKeyword}
        faqs={faqs}
        slug={subtitle || ''}
        onUpdateFaqs={setFaqs}
        onInsertFAQSchema={(validatedFaqs: ArticleFAQ[]) => {
          const faqHtml = `\n<div class="faq-section" itemscope itemtype="https://schema.org/FAQPage">\n  <h2>Frequently Asked Questions</h2>\n  ${validatedFaqs.map(faq => `\n  <div itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">\n    <h3 itemprop="name">${faq.question}</h3>\n    <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">\n      <p itemprop="text">${faq.answer}</p>\n    </div>\n  </div>`).join('\n')}\n</div>`;
          onFieldChange('content', fields.content + '\n' + faqHtml);
          toast.success(`Inserted ${validatedFaqs.length} FAQ(s) with schema markup`);
        }}
      />

      {/* Sources Drawer */}
      <ArticleSourcesDrawer
        open={sourcesPanelOpen}
        onOpenChange={setSourcesPanelOpen}
        sources={sources}
        onUpdateSources={setSources}
        onSyncLinksFromArticle={handleSyncLinksFromArticle}
        brandId={selectedBrandId}
        articleTitle={title}
      />
    </>
  );
};
